import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../context/UserContext';
import { pick, annotationsText } from '../i18n';
import { setAccount, createGroup, updateGroup, deleteGroup } from './store';
import { useAnnotationSnapshot } from './AnnotatedVerse';
import { readSelection, groupRect, selectionRect, copyToClipboard } from './dom';
import { copyText, formatReference, compareVerses } from './anchors';
import { SelectionToolbar } from './SelectionToolbar';
import { NoteEditor } from './NoteEditor';

const PREFS_KEY = 'bibleAnnotationPrefs';
const DEFAULT_PREFS = { style: 'highlight', color: 'yellow' };
function loadPrefs() {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
  catch { return DEFAULT_PREFS; }
}
function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* not essential */ }
}

// Wait for the selection to settle before showing the toolbar. On a phone the
// selection grows while the reader drags its handles, firing selectionchange
// all the way; showing the bar only once it stops keeps it from chasing the
// finger. A mouse release is a clear end, so that shows it straight away.
const SETTLE_MS = 350;
const GAP = 10;

// ── Selection toolbar, note editor and the events that drive them ────────────
// Mounted once per page beside the text. `rootRef` is the element holding the
// verses (AnnotatedVerse spans); selections and taps outside it are ignored.
// `lang` is the interface language, for the toolbar and the copied reference.
// `onListen(ranges)` plays the recording from the first selected verse; leave
// it out and no Listen button shows. `canListen(ranges)`, when given, says
// whether there is a recording with verse timings to play those verses from.
export function AnnotationLayer({ rootRef, lang = 'en', onListen, canListen }) {
  const { user } = useUser();
  const t = pick(annotationsText, lang);
  const snap = useAnnotationSnapshot();

  // Tell the store whose marks to show; the guest list merges in on sign-in.
  useEffect(() => { setAccount(user); }, [user]);

  // null, { kind: 'selection', ranges, rect } or { kind: 'group', groupId }
  const [target, setTarget] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const barRef = useRef(null);
  const noteOpenRef = useRef(false);
  noteOpenRef.current = noteOpen;

  const group = target?.kind === 'group' ? snap.groups.get(target.groupId) : null;
  const current = group ? group[0] : null;

  // A mark deleted (here or by a sync) takes its toolbar with it.
  useEffect(() => {
    if (target?.kind === 'group' && !group && !noteOpen) setTarget(null);
  }, [target, group, noteOpen]);

  useEffect(() => { setCopied(false); }, [target]);

  // ── Reading the selection ──────────────────────────────────────────────
  // Listened for on the document and checked against rootRef at the time,
  // because the verses come and go (The Bible draws them only once a chapter
  // is open) while this stays mounted.
  useEffect(() => {
    let timer = null;
    let mouseDown = false;
    const evaluate = () => {
      if (noteOpenRef.current) return;   // typing a note moves the selection
      const ranges = readSelection(rootRef.current);
      if (ranges) setTarget({ kind: 'selection', ranges, rect: selectionRect() });
      else setTarget(cur => (cur?.kind === 'selection' ? null : cur));
    };
    const settle = ms => { clearTimeout(timer); timer = setTimeout(evaluate, ms); };
    const onSelectionChange = () => { if (!mouseDown) settle(SETTLE_MS); };
    // Touch selection is the browser's own gesture; only a mouse drag is
    // followed through pointer events.
    const onPointerDown = e => {
      if (e.pointerType === 'mouse' && rootRef.current?.contains(e.target)) mouseDown = true;
    };
    const onPointerUp = () => { if (mouseDown) { mouseDown = false; settle(10); } };

    // Tapping a mark (with nothing selected) opens the toolbar on it. Ref
    // markers and links inside a mark keep their own job.
    const onClick = e => {
      if (!e.target.closest || e.target.closest('sup, button, a, input, select, textarea')) return;
      const el = e.target.closest('[data-anno-group]');
      if (!el || !rootRef.current?.contains(el)) return;
      const sel = window.getSelection?.();
      if (sel && !sel.isCollapsed) return;
      setTarget({ kind: 'group', groupId: el.getAttribute('data-anno-group') });
    };

    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    document.addEventListener('click', onClick);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
      document.removeEventListener('click', onClick);
    };
  }, [rootRef]);

  // Tapping anywhere else, or Escape, puts away a toolbar opened on a mark.
  // (A selection's toolbar goes when the selection does.)
  useEffect(() => {
    if (target?.kind !== 'group' || noteOpen) return;
    const away = e => {
      if (barRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[data-anno-group]')) return;
      setTarget(null);
    };
    const esc = e => { if (e.key === 'Escape') setTarget(null); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [target, noteOpen]);

  // ── Placing the toolbar ────────────────────────────────────────────────
  // Written straight to the element's style, so scrolling does not re-render
  // the page. Phones get it below the words, because the system's own
  // copy/paste bubble sits above them; with a mouse it goes above, where the
  // eye already is. Either way it flips to the other side when there is no
  // room, and stays inside the screen.
  const place = useCallback(() => {
    const bar = barRef.current;
    if (!bar || !target) return;
    const rect = target.kind === 'selection'
      ? (selectionRect() || target.rect)
      : groupRect(rootRef.current, target.groupId);
    if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) {
      bar.style.visibility = 'hidden';
      return;
    }
    const w = bar.offsetWidth, h = bar.offsetHeight;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    const above = rect.top - h - GAP, below = rect.bottom + GAP;
    let top;
    if (coarse) top = below + h <= vh - 8 ? below : above >= 8 ? above : vh - h - 8;
    else top = above >= 8 ? above : below + h <= vh - 8 ? below : 8;
    const left = Math.min(Math.max(8, (rect.left + rect.right) / 2 - w / 2), Math.max(8, vw - w - 8));
    bar.style.top = `${Math.round(top)}px`;
    bar.style.left = `${Math.round(left)}px`;
    bar.style.visibility = 'visible';
  }, [target, rootRef]);

  useLayoutEffect(place, [place, snap, copied]);

  useEffect(() => {
    if (!target) return;
    let frame = 0;
    const onMove = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [target, place]);

  // ── Actions ────────────────────────────────────────────────────────────
  const rangesNow = () => (target?.kind === 'selection' ? target.ranges : [...(group || [])].sort(compareVerses));

  // A fresh selection becomes a mark on its first style or colour; from then
  // on the toolbar edits that mark, so a second tap does not stack a copy.
  const apply = patch => {
    const prefs = loadPrefs();
    if (target?.kind === 'selection') {
      const style = patch.style || prefs.style, color = patch.color || prefs.color;
      const groupId = createGroup(target.ranges, { style, color });
      savePrefs({ style, color });
      window.getSelection?.()?.removeAllRanges();
      if (groupId) setTarget({ kind: 'group', groupId });
    } else if (current) {
      // Picking a colour for a note-only mark means the reader wants it seen.
      const next = current.style === 'none' && !patch.style ? { ...patch, style: prefs.style } : patch;
      updateGroup(target.groupId, next);
      savePrefs({ style: next.style || current.style, color: next.color || current.color });
    }
  };

  const onCopy = async () => {
    const ok = await copyToClipboard(copyText(rangesNow(), lang));
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  const onDelete = () => {
    if (target?.kind === 'group') deleteGroup(target.groupId);
    setTarget(null);
  };

  const saveNote = text => {
    const note = text.trim() ? text : null;
    if (target?.kind === 'selection') {
      if (note) {
        const groupId = createGroup(target.ranges, { style: 'none', color: loadPrefs().color, note });
        window.getSelection?.()?.removeAllRanges();
        setTarget(groupId ? { kind: 'group', groupId } : null);
      } else {
        setTarget(null);
      }
    } else if (current) {
      // A note-only mark with its note removed has nothing left to show.
      if (!note && current.style === 'none') { deleteGroup(target.groupId); setTarget(null); }
      else updateGroup(target.groupId, { note });
    }
    setNoteOpen(false);
  };

  // On a phone, focusing the note box clears the selection; if the reader
  // backs out, there is nothing left for the toolbar to point at.
  const cancelNote = () => {
    setNoteOpen(false);
    if (target?.kind === 'selection' && !readSelection(rootRef.current)) setTarget(null);
  };

  if (!target) return null;
  const ranges = rangesNow();
  return createPortal(
    <>
      {!noteOpen && (
        <SelectionToolbar ref={barRef} t={t} current={current} copied={copied}
          onCopy={onCopy} onNote={() => setNoteOpen(true)} onOpenNote={() => setNoteOpen(true)}
          onStyle={style => apply({ style })} onColor={color => apply({ color })}
          onDelete={onDelete}
          onListen={onListen && (!canListen || canListen(ranges)) ? () => onListen(ranges) : undefined} />
      )}
      {noteOpen && (
        <NoteEditor t={t} title={formatReference(ranges, lang)} initial={current?.note || ''}
          onSave={saveNote} onCancel={cancelNote}
          onDelete={current ? () => saveNote('') : undefined} />
      )}
    </>,
    document.body
  );
}
